import React, { useCallback, useEffect, useRef, useState } from 'react';
import { ArrowPathIcon } from '@heroicons/react/24/outline';
import saverulesDashboardService, {
  SaverulesDashboardSummary,
  SaverulesGap,
} from '../../services/saverulesDashboard.service';
import SaverulesDrilldownModal, { DrilldownTarget } from './SaverulesDrilldownModal';
import ProDetailSlideOver from '../../components/pro/ProDetailSlideOver';

const REFRESH_MS = 30000;
const DAY_OPTIONS = [1, 7, 14, 30];

const fmt = (n: number | null | undefined) => (n ?? 0).toLocaleString();

function agoText(mins: number | null): string {
  if (mins == null) return 'never';
  if (mins < 120) return `${mins}m ago`;
  return `${Math.floor(mins / 60)}h ${mins % 60}m ago`;
}

function pctColor(pct: number): string {
  if (pct >= 99) return 'text-emerald-600 dark:text-emerald-400';
  if (pct >= 95) return 'text-amber-600 dark:text-amber-400';
  return 'text-red-600 dark:text-red-400';
}

const OWNER_BAR: Record<string, string> = {
  driver: 'bg-amber-500',
  basesave: 'bg-violet-500',
  reconciler: 'bg-teal-500',
  parked: 'bg-slate-400 dark:bg-slate-500',
  portable: 'bg-sky-500',
};
const OWNER_LABEL: Record<string, string> = {
  // sr_driver is PORTED (rules/leg_driver.py). This bucket used to read
  // "sr_driver (epic)", which said "outstanding work, nothing to do" and parked the
  // board's top gap for weeks — a gap here is a port DEFECT to chase. Note v1 emits
  // dispatch_update from deleteDispatch, which sr_legdispatch also calls, so confirm
  // the v1 source before blaming leg_driver. See _owner_for's DRIVER_EFFECTS caveat.
  driver: 'sr_driver / dispatch (defects)',
  basesave: 'base-save (out of scope)',
  reconciler: 'reconciler (out of scope)',
  parked: 'parked',
  portable: 'portable',
};
// gwitrace milestone-path gaps are recolored to this hue (distinct from every owner
// color above) so the trace path can be watched on its own; overrides the owner color.
const TRACE_BAR = 'bg-fuchsia-500';
const TRACE_LABEL = 'gwitrace (trace path)';

// Why a class of shadow row is held out of the parity numbers. Kept visible (rather
// than filtered away in SQL and forgotten) so every exclusion stays auditable.
const EXCLUDED_KIND_LABEL: Record<string, string> = {
  v1_defect: 'v1 defect',
  cutover_only: 'cutover-only',
  out_of_scope: 'out of scope',
  artifact: 'measurement artifact',
};
const EXCLUDED_KIND_STYLE: Record<string, string> = {
  v1_defect: 'text-amber-600 dark:text-amber-400 border-amber-400/50',
  cutover_only: 'text-sky-600 dark:text-sky-400 border-sky-400/50',
  out_of_scope: 'text-violet-600 dark:text-violet-400 border-violet-400/50',
  artifact: 'text-slate-600 dark:text-slate-300 border-slate-400/50',
};

// The liveness banner reads TWO independent backend flags, because they are different
// problems: `stale` = no heartbeat for 6h, the feed is DARK and every number on this
// page is frozen; `errored` = the feed is live but the shadow threw on some saves, so
// only those saves lack parity coverage. Dark outranks erroring when both are true.
// Keeping them separate is deliberate: they were one flag until a recurring template
// error held the light red against a 0-minute-old heartbeat, and an alarm that is
// always on is an alarm nobody reads.
type LivenessKey = 'dark' | 'errored' | 'live';
const LIVENESS_TONE: Record<LivenessKey, { box: string; dot: string; label: string }> = {
  dark: {
    box: 'border-red-300 dark:border-red-800 bg-red-50 dark:bg-red-950/40 text-red-700 dark:text-red-200',
    dot: 'bg-red-500',
    label: 'Shadow pipeline STALE',
  },
  errored: {
    box: 'border-amber-300 dark:border-amber-800 bg-amber-50 dark:bg-amber-950/40 text-amber-700 dark:text-amber-200',
    dot: 'bg-amber-500 animate-pulse',
    label: 'Shadow pipeline live — errors logged',
  },
  live: {
    box: 'border-emerald-300 dark:border-emerald-800 bg-emerald-50 dark:bg-emerald-950/40 text-emerald-700 dark:text-emerald-200',
    dot: 'bg-emerald-500 animate-pulse',
    label: 'Shadow pipeline live',
  },
};

const SaverulesDashboard: React.FC = () => {
  const [days, setDays] = useState(7);
  const [data, setData] = useState<SaverulesDashboardSummary | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);
  const [drilldown, setDrilldown] = useState<DrilldownTarget | null>(null);
  const [selectedPro, setSelectedPro] = useState<string | number | null>(null);
  const daysRef = useRef(days);
  daysRef.current = days;

  const load = useCallback(async (signal?: AbortSignal, spinner = false) => {
    if (spinner) setLoading(true);
    else setRefreshing(true);
    try {
      const res = await saverulesDashboardService.getSummary(daysRef.current, signal);
      setData(res);
      setError(null);
      setLastUpdated(new Date());
    } catch (e: any) {
      if (e?.name === 'CanceledError' || e?.code === 'ERR_CANCELED') return;
      setError(e?.response?.data?.detail ?? e?.message ?? 'Failed to load dashboard');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  // Initial load + reload whenever the window changes.
  useEffect(() => {
    const controller = new AbortController();
    load(controller.signal, true);
    return () => controller.abort();
  }, [days, load]);

  // Background polling.
  useEffect(() => {
    if (!autoRefresh) return;
    const id = setInterval(() => load(), REFRESH_MS);
    return () => clearInterval(id);
  }, [autoRefresh, load]);

  const parity = data?.parity;
  const health = data?.health;
  // Undefined on API builds predating the write-verify audit — the panel stays hidden.
  const wv = data?.writeVerify;
  // Undefined on API builds predating the *current mirror-drift panel — panel hidden.
  const mirror = data?.mirror;
  // `auditedSaves` is the coverage denominator. A clean save writes NO divergence rows,
  // so `rows === 0` on its own cannot tell "everything landed" from "this writer never
  // ran here" — and the second is the true reading while native mode is off everywhere.
  // Undefined (older API) is treated as unknown, not as covered.
  const wvAudited = wv?.auditedSaves ?? 0;
  const wvCovered = wvAudited > 0;
  // Undefined on API builds predating the known-non-divergence exclusion — panel hidden.
  const excluded = data?.excluded;
  const reconciler = data?.reconciler;
  const cleanPct = parity && parity.cleanOf ? (100 * parity.cleanSaves) / parity.cleanOf : 0;
  const cleanNoExtraPct =
    parity && parity.cleanOf && parity.cleanSavesNoExtra != null
      ? (100 * parity.cleanSavesNoExtra) / parity.cleanOf
      : null;
  const reconPct =
    reconciler && reconciler.cleanOf ? (100 * reconciler.cleanSaves) / reconciler.cleanOf : 0;
  const gapMax = Math.max(1, ...(data?.gaps ?? []).map((g) => g.n));
  // `errored` is undefined on API builds predating the stale/errored split — treat that
  // as not-erroring, same convention as the optional panels above.
  const liveness: LivenessKey = health?.stale ? 'dark' : health?.errored ? 'errored' : 'live';
  const tone = LIVENESS_TONE[liveness];

  return (
    <div className="p-6 text-slate-900 dark:text-slate-100 flex flex-col gap-4 w-full h-full overflow-y-auto">
      {/* Header */}
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-baseline gap-3">
          <h1 className="text-lg font-semibold">Saverules Parity Dashboard</h1>
          <span className="text-xs text-slate-500 dark:text-slate-400">
            shadow-mode · realtime
          </span>
        </div>
        <div className="flex items-center gap-3 text-sm">
          <label className="flex items-center gap-2 text-slate-600 dark:text-slate-400">
            Window
            <select
              className="bg-white dark:bg-slate-950 border border-slate-300 dark:border-slate-700 rounded-md px-2 py-1 text-sm"
              value={days}
              onChange={(e) => setDays(Number(e.target.value))}
            >
              {DAY_OPTIONS.map((d) => (
                <option key={d} value={d}>
                  {d === 1 ? '24 hours' : `${d} days`}
                </option>
              ))}
            </select>
          </label>
          <label className="flex items-center gap-2 text-slate-600 dark:text-slate-400 select-none">
            <input
              type="checkbox"
              checked={autoRefresh}
              onChange={(e) => setAutoRefresh(e.target.checked)}
            />
            Auto (30s)
          </label>
          <button
            onClick={() => load()}
            className="flex items-center gap-1.5 rounded-md border border-slate-300 dark:border-slate-700 px-2.5 py-1 text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-800"
          >
            <ArrowPathIcon className={`h-4 w-4 ${refreshing ? 'animate-spin' : ''}`} />
            Refresh
          </button>
          {lastUpdated && (
            <span className="text-xs text-slate-400">
              updated {lastUpdated.toLocaleTimeString()}
            </span>
          )}
        </div>
      </div>

      {error && (
        <div className="border border-red-300 dark:border-red-800 bg-red-50 dark:bg-red-950/40 text-red-700 dark:text-red-200 rounded-md px-4 py-3 text-sm">
          {error}
        </div>
      )}

      {loading && !data ? (
        <div className="text-slate-600 dark:text-slate-400">Loading parity numbers…</div>
      ) : data ? (
        <>
          {/* Liveness banner — STALE (dark feed) invalidates everything below it; errors
              alone do not, so they get their own amber state (see LIVENESS_TONE). */}
          {health && (
            <div
              className={
                'rounded-md px-4 py-3 text-sm border flex items-center justify-between gap-4 flex-wrap ' +
                tone.box
              }
            >
              <div className="flex items-center gap-2 font-medium">
                <span className={'inline-block h-2.5 w-2.5 rounded-full ' + tone.dot} />
                {tone.label}
              </div>
              <div className="flex items-center gap-5 text-xs">
                <span>last OK heartbeat: {agoText(health.minsSinceOk)}</span>
                <span>saves / 24h: {fmt(health.saves24h)}</span>
                {health.errors.rows > 0 && (
                  <span className="font-semibold">
                    {fmt(health.errors.rows)} pipeline errors / {fmt(health.errors.saves)} saves
                  </span>
                )}
              </div>
            </div>
          )}

          {/* Parity KPIs */}
          {parity && (
            <Section title="Shadow parity — production volume" subtitle={parity.window}>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-7 gap-3">
                <Stat label="Saves shadowed" value={fmt(parity.saves)} />
                <Stat
                  label="Clean saves"
                  value={`${cleanPct.toFixed(2)}%`}
                  valueClass={pctColor(cleanPct)}
                  sub={`${fmt(parity.cleanSaves)} / ${fmt(parity.cleanOf)}`}
                />
                {cleanNoExtraPct != null && (
                  <Stat
                    label="Clean saves (ignoring extras)"
                    value={`${cleanNoExtraPct.toFixed(2)}%`}
                    valueClass={pctColor(cleanNoExtraPct)}
                    sub={`${fmt(parity.cleanSavesNoExtra)} / ${fmt(parity.cleanOf)} · value diffs only`}
                  />
                )}
                <Stat
                  label="Value diffs"
                  value={fmt(parity.valueDiffs)}
                  /* Only the UNEXPLAINED ones are a problem: a diff traced to a v1-side
                     defect is a finding, not port debt, so it must not paint this red. */
                  valueClass={
                    parity.valueDiffs - (parity.valueDiffsV1Defect ?? 0)
                      ? 'text-red-600 dark:text-red-400'
                      : parity.valueDiffs
                      ? 'text-teal-600 dark:text-teal-400'
                      : ''
                  }
                  sub={
                    parity.valueDiffsV1Defect
                      ? `${fmt(parity.valueDiffsV1Defect)} known v1 defect${parity.valueDiffsV1Defect === 1 ? '' : 's'}`
                      : undefined
                  }
                  onClick={() =>
                    setDrilldown({ title: 'Value diffs — Python ≠ V1', section: 'valuediff' })
                  }
                />
                <Stat
                  label="Benign extras"
                  value={fmt(parity.fieldExtra + parity.effectExtra)}
                  onClick={() =>
                    setDrilldown({ title: 'Benign extras — Python wrote, V1 did not', section: 'extra' })
                  }
                />
                <Stat label="Matched writes" value={fmt(parity.matchedWrites)} />
                <Stat
                  label="Missing writes"
                  value={fmt(parity.missingWrites)}
                  sub={
                    parity.missingWrites
                      ? `${Math.round((100 * parity.srDriverMissing) / parity.missingWrites)}% sr_driver · ` +
                        `${Math.round((100 * parity.baseSaveMissing) / parity.missingWrites)}% base-save`
                      : undefined
                  }
                  onClick={() =>
                    setDrilldown({ title: 'Missing writes — V1 wrote, Python did not', section: 'missing' })
                  }
                />
              </div>
              {parity.byKind && (
                <div className="mt-3 flex flex-wrap gap-x-5 gap-y-1 text-xs text-slate-600 dark:text-slate-400">
                  <span className="font-medium text-slate-500 dark:text-slate-400">By kind:</span>
                  {(
                    [
                      ['missing', 'missing', 'missing'],
                      ['effect_missing', 'effect_missing', 'missing'],
                      ['extra', 'extra', 'extra'],
                      ['effect_extra', 'effect_extra', 'extra'],
                      ['value_diff', 'value_diff', 'valuediff'],
                    ] as const
                  ).map(([key, label, section]) => (
                    <button
                      key={key}
                      type="button"
                      className="flex items-center gap-1 hover:underline"
                      onClick={() => setDrilldown({ title: `${label} — ${parity.window}`, section })}
                    >
                      <span>{label}</span>
                      <span className="tabular-nums font-semibold text-slate-800 dark:text-slate-100">
                        {fmt(parity.byKind?.[key])}
                      </span>
                    </button>
                  ))}
                </div>
              )}
              {parity.valueDiffDetail.length > 0 && (
                <div className="mt-4">
                  <div className="text-xs font-medium text-slate-500 dark:text-slate-400 mb-1">
                    Value-diff detail — “review” needs triage; “v1 defect” is already traced to a v1 bug
                  </div>
                  <div className="overflow-x-auto">
                    <table className="min-w-full text-xs">
                      <thead className="text-slate-500 dark:text-slate-400 text-left">
                        <tr>
                          <th className="py-1 pr-4">Field</th>
                          <th className="py-1 pr-4">Rule</th>
                          <th className="py-1 pr-4">Python</th>
                          <th className="py-1 pr-4">V1</th>
                          <th className="py-1 pr-4">Rows</th>
                          <th className="py-1">Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {parity.valueDiffDetail.map((v, i) => (
                          <tr key={i} className="border-t border-slate-100 dark:border-slate-800">
                            <td className="py-1 pr-4">{v.field}</td>
                            <td className="py-1 pr-4">{v.rule}</td>
                            <td className="py-1 pr-4 text-sky-700 dark:text-sky-300">{v.python}</td>
                            <td className="py-1 pr-4 text-amber-700 dark:text-amber-300">{v.v1}</td>
                            <td className="py-1 pr-4 tabular-nums">{v.n}</td>
                            <td className="py-1">
                              {v.v1Defect ? (
                                <span
                                  className="cursor-help rounded bg-teal-100 px-1.5 py-0.5 text-teal-800 dark:bg-teal-950/50 dark:text-teal-300"
                                  title={`${v.v1Defect.label} — ${v.v1Defect.note}`}
                                >
                                  v1 defect
                                </span>
                              ) : (
                                <span className="text-slate-400">review</span>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </Section>
          )}

          {/* Top gaps by volume */}
          {data.gaps.length > 0 && (
            <Section title="Top gaps by production volume" subtitle="missing / diff occurrences">
              <div className="flex flex-col gap-2">
                {data.gaps.map((g: SaverulesGap) => {
                  const isTrace = g.source === 'trace';
                  const barColor = isTrace ? TRACE_BAR : (OWNER_BAR[g.owner] ?? 'bg-slate-400');
                  return (
                  <button
                    key={`${g.name}__${g.source ?? ''}`}
                    onClick={() => setDrilldown({ title: `Gap · ${g.name}`, section: 'gap', bucket: g.name })}
                    className="flex items-center gap-3 rounded px-1 -mx-1 hover:bg-slate-50 dark:hover:bg-slate-800/50"
                  >
                    <div className="w-48 shrink-0 text-left text-sm truncate flex items-center gap-1.5" title={g.name}>
                      {isTrace && (
                        <span className="text-[0.5625rem] uppercase tracking-wide font-semibold text-fuchsia-600 dark:text-fuchsia-400 border border-fuchsia-400/50 rounded px-1 py-px shrink-0">
                          trace
                        </span>
                      )}
                      <span className="truncate">{g.name}</span>
                    </div>
                    <div className="flex-1 bg-slate-100 dark:bg-slate-800 rounded h-5 overflow-hidden">
                      <div
                        className={`h-full ${barColor}`}
                        style={{ width: `${Math.max(2, (100 * g.n) / gapMax)}%` }}
                      />
                    </div>
                    <div className="w-16 shrink-0 text-right text-sm tabular-nums">{fmt(g.n)}</div>
                  </button>
                  );
                })}
              </div>
              <div className="flex items-center flex-wrap gap-4 mt-3 text-xs text-slate-500 dark:text-slate-400">
                {Object.keys(OWNER_BAR).map((o) => (
                  <span key={o} className="flex items-center gap-1.5">
                    <span className={`inline-block h-2.5 w-2.5 rounded-sm ${OWNER_BAR[o]}`} />
                    {OWNER_LABEL[o]}
                  </span>
                ))}
                <span className="flex items-center gap-1.5">
                  <span className={`inline-block h-2.5 w-2.5 rounded-sm ${TRACE_BAR}`} />
                  {TRACE_LABEL}
                </span>
              </div>
            </Section>
          )}

          {/* Known non-divergences — held out of every number above */}
          {excluded && excluded.rows > 0 && (
            <Section
              title="Held out of the numbers above"
              subtitle={`${fmt(excluded.rows)} rows · not cutover risk`}
            >
              <p className="text-xs text-slate-500 dark:text-slate-400 mb-3">
                These are real shadow rows, kept in the log but excluded from parity,
                gaps and the clean-save rate — so the counts above mean “would break
                when we flip live”. Click any row for the evidence.
              </p>
              <div className="flex flex-col gap-2">
                {excluded.bySignature
                  .filter((s) => s.n > 0)
                  .map((s) => (
                    <button
                      key={s.key}
                      onClick={() =>
                        setDrilldown({
                          title: `Held out · ${s.label}`,
                          section: 'excluded',
                          bucket: s.key,
                        })
                      }
                      className="text-left rounded border border-slate-200 dark:border-slate-700 px-3 py-2 hover:bg-slate-50 dark:hover:bg-slate-800/50"
                    >
                      <div className="flex items-center gap-2 flex-wrap">
                        <span
                          className={`text-[0.5625rem] uppercase tracking-wide font-semibold rounded px-1 py-px border ${
                            EXCLUDED_KIND_STYLE[s.kind] ?? EXCLUDED_KIND_STYLE.out_of_scope
                          }`}
                        >
                          {EXCLUDED_KIND_LABEL[s.kind] ?? s.kind}
                        </span>
                        <span className="text-sm font-medium">{s.label}</span>
                        <span className="ml-auto text-sm tabular-nums text-slate-500 dark:text-slate-400">
                          {fmt(s.n)} rows · {fmt(s.saves)} saves
                        </span>
                      </div>
                      <p className="mt-1 text-xs text-slate-600 dark:text-slate-400">{s.why}</p>
                      <p className="mt-1 text-xs text-slate-500 dark:text-slate-500 italic">
                        {s.impact}
                      </p>
                    </button>
                  ))}
              </div>
            </Section>
          )}

          {/* Invoice reconciler parity */}
          {reconciler && reconciler.saves > 0 && (
            <Section title="Invoice reconciler — parity vs legacy" subtitle={reconciler.window}>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
                <Stat label="Reconciler saves" value={fmt(reconciler.saves)} />
                <Stat
                  label="Clean (no real op)"
                  value={`${reconPct.toFixed(2)}%`}
                  valueClass={pctColor(reconPct)}
                  sub={`${fmt(reconciler.cleanSaves)} / ${fmt(reconciler.cleanOf)}`}
                />
                <Stat
                  label="Real ops"
                  value={fmt(reconciler.real)}
                  valueClass={reconciler.real ? 'text-red-600 dark:text-red-400' : ''}
                  sub={`${fmt(reconciler.benign)} benign`}
                  onClick={() =>
                    setDrilldown({ title: 'Reconciler — real divergences', section: 'reconciler', optype: 'real' })
                  }
                />
                <Stat label="Match / skip" value={`${fmt(reconciler.match)} / ${fmt(reconciler.skip)}`} />
                <Stat
                  label="Would clear a live fee"
                  value={fmt(reconciler.cutover.pros)}
                  sub={`$${reconciler.cutover.dollars.toLocaleString()}`}
                  onClick={() =>
                    setDrilldown({ title: 'Reconciler — would clear a live fee', section: 'reconciler', optype: 'clear' })
                  }
                />
              </div>
              {Object.keys(reconciler.byArea).length > 0 && (
                <div className="mt-3 flex flex-wrap gap-x-6 gap-y-1 text-xs text-slate-600 dark:text-slate-400">
                  {Object.entries(reconciler.byArea).map(([area, a]) => (
                    <button
                      key={area}
                      onClick={() =>
                        setDrilldown({ title: `Reconciler · ${area}`, section: 'reconciler', area })
                      }
                      className="rounded px-1 hover:bg-slate-100 dark:hover:bg-slate-800"
                    >
                      <span className="font-medium text-slate-700 dark:text-slate-300">{area}</span>:{' '}
                      {fmt(a.ops)} ops ({fmt(a.real)} real / {fmt(a.benign)} benign)
                    </button>
                  ))}
                </div>
              )}
              {reconciler.causes && Object.keys(reconciler.causes).length > 0 && (
                <div className="mt-4 border-t border-slate-100 dark:border-slate-800 pt-3">
                  <div className="mb-1 text-xs font-medium text-slate-700 dark:text-slate-300">
                    Real divergences by cause{' '}
                    <span className="font-normal text-slate-400 dark:text-slate-500">
                      v2-quote = transient
                    </span>
                  </div>
                  <div className="flex flex-wrap gap-x-6 gap-y-1 text-xs text-slate-600 dark:text-slate-400">
                    {Object.entries(reconciler.causes).map(([area, c]) => {
                      const total = c.v2_quote + c.other || 1;
                      const pct = Math.round((100 * c.v2_quote) / total);
                      return (
                        <span key={area}>
                          <span className="font-medium capitalize text-slate-700 dark:text-slate-300">
                            {area}
                          </span>
                          :{' '}
                          <span className="text-violet-600 dark:text-violet-400">
                            {fmt(c.v2_quote)} v2-quote ({pct}%)
                          </span>{' '}
                          / {fmt(c.other)} other
                        </span>
                      );
                    })}
                  </div>
                  <p className="mt-2 max-w-3xl text-xs text-slate-500 dark:text-slate-400">
                    <span className="font-medium">v2-quote</span> divergences are expected and
                    self-clearing: the reconciler reads chassis/fuel config from the local quote
                    tables, which don&apos;t yet carry the v2 quotes, so it can&apos;t resolve a
                    rate — they resolve as the v2 migration proceeds. The{' '}
                    <span className="font-medium">other</span> remainder is dominated by
                    not-yet-dated / just-delivered orders (also transient).
                  </p>
                </div>
              )}
            </Section>
          )}

          {/* Save latency */}
          {data.timing.segments.length > 0 && (
            <Section
              title="Save latency"
              subtitle={`mean ms over ${fmt(data.timing.saves)} timed saves`}
            >
              <div className="overflow-x-auto">
                <table className="min-w-full text-sm">
                  <thead className="text-slate-500 dark:text-slate-400 text-left text-xs">
                    <tr>
                      <th className="py-1 pr-6">Segment</th>
                      <th className="py-1 pr-6 text-right">Mean ms</th>
                      <th className="py-1 pr-6 text-right">Max ms</th>
                      <th className="py-1 pr-6 text-right">n</th>
                      <th className="py-1">Path</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.timing.segments.map((s) => (
                      <tr key={s.segment} className="border-t border-slate-100 dark:border-slate-800">
                        <td className="py-1 pr-6">{s.segment}</td>
                        <td className="py-1 pr-6 text-right tabular-nums">{s.meanMs.toFixed(1)}</td>
                        <td className="py-1 pr-6 text-right tabular-nums">{s.maxMs.toFixed(1)}</td>
                        <td className="py-1 pr-6 text-right tabular-nums">{fmt(s.n)}</td>
                        <td className="py-1">
                          <span
                            className={
                              'text-xs px-1.5 py-0.5 rounded ' +
                              (s.native
                                ? 'bg-sky-100 dark:bg-sky-950/60 text-sky-700 dark:text-sky-300'
                                : 'bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-400')
                            }
                          >
                            {s.native ? 'native' : s.segment === 'cmsapi' ? 'cmsapi/perl' : '—'}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Section>
          )}

          {/* Native write verification — every native save re-reads the rows it wrote
              and logs any column the DB stored differently. Zero rows is healthy, so
              the panel states that explicitly rather than hiding when clean. */}
          {wv && (
            <Section
              title="Native write verification"
              subtitle={
                wv.rows > 0
                  ? `${fmt(wv.rows)} column(s) did not persist as written · ${fmt(wv.pros)} pro(s)`
                  : wvCovered
                    ? `every column read back as written · ${fmt(wvAudited)} save(s) audited`
                    : 'no coverage — this writer has not run in the window'
              }
            >
              {wv.rows === 0 ? (
                wvCovered ? (
                  <p className="text-sm text-slate-500 dark:text-slate-400">
                    No divergences across {fmt(wvAudited)} audited save(s) in the{' '}
                    {wv.window}
                    {wv.checkedRows ? ` (${fmt(wv.checkedRows)} rows re-read)` : ''}. Each
                    native save re-reads every row it wrote — the authoritative tables and
                    the orderscurrent / legscurrent read copies — and compares each column
                    against the value it sent. Silent coercion (a display-format date
                    landing as 0000-00-00), truncation, a concurrent writer, or a mirror
                    double-write that matched no row would appear here.
                  </p>
                ) : (
                  <p className="text-sm text-slate-500 dark:text-slate-400">
                    <span className="font-medium text-amber-600 dark:text-amber-400">
                      No coverage — not a clean bill.
                    </span>{' '}
                    These rows only exist where the native writer commits, and no
                    environment runs <code>SAVERULES_SAVE_MODE=native</code> yet, so zero
                    divergences here proves nothing. The bulk tools (per-diem recalc, EDI
                    attach) do reach that writer against prod, so this fills in as they
                    fire. Until then the mirror-drift panel below is the live signal — it
                    reads <code>cms_table_sync</code>&apos;s repair log and so covers every
                    writer, native or not.
                  </p>
                )
              ) : (
                <div className="overflow-x-auto">
                  <table className="min-w-full text-xs">
                    <thead className="text-slate-500 dark:text-slate-400 text-left">
                      <tr>
                        <th className="py-1 pr-4">Table</th>
                        <th className="py-1 pr-4">Column</th>
                        <th className="py-1 pr-4 text-right">Count</th>
                        <th className="py-1 pr-4 text-right">Pros</th>
                        <th className="py-1 pr-4">Last seen</th>
                        <th className="py-1">Sent → stored</th>
                      </tr>
                    </thead>
                    <tbody>
                      {wv.byColumn.map((c) => (
                        <tr
                          key={`${c.table}.${c.field}`}
                          onClick={() =>
                            setDrilldown({
                              title: `Write verify · ${c.table}.${c.field}`,
                              section: 'write_verify',
                              bucket: `${c.table}.${c.field}`,
                            })
                          }
                          className="border-t border-slate-100 dark:border-slate-800 cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-800/40"
                        >
                          <td className="py-1 pr-4">
                            {c.table}
                            {c.mirror && (
                              <span
                                title="Read-copy mirror: the double-write matched no row. Not a DB coercion — cms_table_sync cannot repair a drifted legnum, it duplicates the leg."
                                className="ml-1.5 rounded px-1 py-0.5 text-[10px] font-medium bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300"
                              >
                                mirror
                              </span>
                            )}
                          </td>
                          <td className="py-1 pr-4">{c.field}</td>
                          <td className="py-1 pr-4 text-right tabular-nums">{fmt(c.n)}</td>
                          <td className="py-1 pr-4 text-right tabular-nums">{fmt(c.pros)}</td>
                          <td className="py-1 pr-4">{c.lastSeen}</td>
                          <td className="py-1 text-slate-500 dark:text-slate-400 max-w-md truncate">
                            {c.sample}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </Section>
          )}

          {/* *current mirror drift. Unlike write verification this is NOT per-save and
              NOT native-only: it reads cms_table_sync's own repair log, so it covers
              every writer that touches the *all tables — native, cmsapi, V1 Perl, bulk
              jobs. That makes it the live signal while native mode is off everywhere. */}
          {mirror && (
            <Section
              title="*current mirror drift"
              subtitle={
                mirror.available
                  ? `${fmt(mirror.updated)} row(s) repaired by cms_table_sync · ${mirror.window}`
                  : 'sync_job_history unavailable here'
              }
            >
              {!mirror.available ? (
                <p className="text-sm text-slate-500 dark:text-slate-400">
                  <code>sync_job_history</code> is not readable from this database, so the
                  repair log cannot be shown. The numbers are not zero — they are unknown.
                </p>
              ) : (
                <>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-3">
                    <Stat
                      label="Rows repaired"
                      value={fmt(mirror.updated)}
                      sub={`median ${fmt(mirror.medianDaily)}/day`}
                    />
                    <Stat
                      label="Legs duplicated"
                      value={fmt(mirror.inserted)}
                      valueClass={
                        mirror.legIdentityDrift ? 'text-rose-600 dark:text-rose-400' : undefined
                      }
                      sub={mirror.legIdentityDrift ? 'leg identity drift' : 'clean'}
                    />
                    <Stat
                      label="Worst single run"
                      value={fmt(mirror.worstUpdated)}
                      valueClass={mirror.spikeDay ? 'text-amber-600 dark:text-amber-400' : undefined}
                      sub={mirror.spikeDay ? `spike ${mirror.spikeDay}` : 'no spike'}
                    />
                    <Stat
                      label="Runs finding drift"
                      value={`${fmt(mirror.runsWithDrift)} / ${fmt(mirror.runs)}`}
                      sub={`${fmt(mirror.failedRuns)} failed`}
                    />
                  </div>

                  {mirror.byDay.length > 0 && (
                    <div className="space-y-1 mb-3">
                      {mirror.byDay.map((dRow) => {
                        const max = Math.max(1, ...mirror.byDay.map((x) => x.updated));
                        const isSpike = dRow.day === mirror.spikeDay;
                        return (
                          <div key={dRow.day} className="flex items-center gap-2 text-xs">
                            <span className="w-24 shrink-0 text-slate-500 dark:text-slate-400 tabular-nums">
                              {dRow.day}
                            </span>
                            <div className="flex-1 h-2 rounded bg-slate-100 dark:bg-slate-800 overflow-hidden">
                              <div
                                className={`h-full rounded ${
                                  dRow.inserted > 0
                                    ? 'bg-rose-500'
                                    : isSpike
                                      ? 'bg-amber-500'
                                      : 'bg-sky-500'
                                }`}
                                style={{ width: `${(dRow.updated / max) * 100}%` }}
                              />
                            </div>
                            <span className="w-32 shrink-0 text-right tabular-nums text-slate-500 dark:text-slate-400">
                              {fmt(dRow.updated)}
                              {dRow.inserted > 0 ? ` · +${fmt(dRow.inserted)} ins` : ''}
                            </span>
                          </div>
                        );
                      })}
                    </div>
                  )}

                  <p className="text-sm text-slate-500 dark:text-slate-400">
                    {mirror.legIdentityDrift ? (
                      <>
                        <span className="font-medium text-rose-600 dark:text-rose-400">
                          Leg identity drift.
                        </span>{' '}
                        The job inserted {fmt(mirror.inserted)} leg row(s). It repairs a
                        drifted column by copying it, but <code>legnum</code> is one of its
                        JOIN keys — so a leg whose number the mirror missed is{' '}
                        <strong>duplicated, not repaired</strong>. Treat any non-zero here
                        as a write-path bug, never routine lag.
                      </>
                    ) : (
                      <>
                        Column-level lag only — no duplicate legs, so no leg-identity
                        drift. A non-zero <strong>legs duplicated</strong> would mean a
                        mirror write missed a <code>legnum</code>, which this job
                        structurally cannot repair.
                      </>
                    )}
                  </p>
                </>
              )}
            </Section>
          )}

          {/* Pipeline errors */}
          {health && health.errors.bySignature.length > 0 && (
            <Section title="Pipeline errors" subtitle="shadow_error rows by stage + signature">
              <div className="overflow-x-auto">
                <table className="min-w-full text-xs">
                  <thead className="text-slate-500 dark:text-slate-400 text-left">
                    <tr>
                      <th className="py-1 pr-4">Stage</th>
                      <th className="py-1 pr-4">Exception</th>
                      <th className="py-1 pr-4 text-right">Count</th>
                      <th className="py-1 pr-4">Last seen</th>
                      <th className="py-1">Sample</th>
                    </tr>
                  </thead>
                  <tbody>
                    {health.errors.bySignature.map((e, i) => (
                      <tr
                        key={i}
                        onClick={() =>
                          setDrilldown({ title: `Pipeline errors · ${e.stage ?? ''}`, section: 'error' })
                        }
                        className="border-t border-slate-100 dark:border-slate-800 cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-800/40"
                      >
                        <td className="py-1 pr-4">{e.stage}</td>
                        <td className="py-1 pr-4">{e.exc}</td>
                        <td className="py-1 pr-4 text-right tabular-nums">{fmt(e.n)}</td>
                        <td className="py-1 pr-4">{e.lastSeen}</td>
                        <td className="py-1 text-slate-500 dark:text-slate-400 max-w-md truncate">
                          {e.sample}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Section>
          )}

          <div className="text-xs text-slate-400 pt-1">
            {fmt(data.portedRules)} rules registered · window {data.window}
            {data.asOf ? ` · newest row ${data.asOf}` : ''}
          </div>
        </>
      ) : null}

      <SaverulesDrilldownModal
        target={drilldown}
        days={days}
        onClose={() => setDrilldown(null)}
        onOpenPro={(pro) => setSelectedPro(pro)}
      />
      <ProDetailSlideOver
        pronumber={selectedPro}
        isOpen={selectedPro !== null}
        onClose={() => setSelectedPro(null)}
      />
    </div>
  );
};

function Section({
  title,
  subtitle,
  children,
}: {
  title: string;
  subtitle?: string;
  children: React.ReactNode;
}) {
  return (
    <div className="rounded-lg border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-4">
      <div className="flex items-baseline justify-between mb-3">
        <h2 className="text-sm font-semibold">{title}</h2>
        {subtitle && <span className="text-xs text-slate-400">{subtitle}</span>}
      </div>
      {children}
    </div>
  );
}

function Stat({
  label,
  value,
  sub,
  valueClass,
  onClick,
}: {
  label: string;
  value: string;
  sub?: string;
  valueClass?: string;
  onClick?: () => void;
}) {
  const base =
    'rounded-md border border-slate-100 dark:border-slate-800 bg-slate-50 dark:bg-slate-950/40 px-3 py-2 text-left';
  const inner = (
    <>
      <div className="text-xs text-slate-500 dark:text-slate-400">{label}</div>
      <div className={`text-xl font-semibold tabular-nums ${valueClass ?? ''}`}>{value}</div>
      {sub && <div className="text-xs text-slate-400 tabular-nums">{sub}</div>}
    </>
  );
  if (onClick) {
    return (
      <button
        onClick={onClick}
        className={`${base} w-full cursor-pointer hover:border-sky-400 hover:bg-sky-50 dark:hover:border-sky-700 dark:hover:bg-sky-950/30`}
      >
        {inner}
      </button>
    );
  }
  return <div className={base}>{inner}</div>;
}

export default SaverulesDashboard;
